/**
 * Decision Room → Proposal section, PROSPECT side (2026-09-07, flag `proposals`).
 *
 * Renders the live proposal for this room: blocks in order (narrative,
 * the call's trailer through the same embedded ShareViewer the Demos
 * section uses, a quote table, a document card, media), the buyer's
 * optional-line toggles with the total following, a comment thread
 * that rides the room conversation, and Accept.
 *
 * Accepting records intent (and notifies the seller); the legal
 * signature stays in the e-sign vendor. Everything here goes through
 * the room's public routes on the room credential.
 *
 * Beacons: the proposal view fires once per load after a real human
 * signal (utils/humanPresence — a mail filter's headless fetch never
 * becomes "the CFO opened the proposal"); per-block dwell posts the
 * seconds a block was on screen when it scrolls away or unmounts.
 *
 * Light-locked, inline styles, like the rest of the prospect view.
 */

import { useEffect, useRef, useState } from 'react';
import InlineMarkdown from './InlineMarkdown';
import ShareViewer from './ShareViewer';
import { API_BASE, moneyLabel, formatDate, formatWhen } from '../utils/apiRequest';
import { whenHuman } from '../utils/humanPresence';
import { computeTotals } from '../utils/proposalTotals';

function pub(slug, token, path = '') {
  return `${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/proposal${path}?t=${encodeURIComponent(token)}`;
}

async function post(url, body) {
  const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(data.error || `Request failed (${r.status})`); e.status = r.status; throw e; }
  return data;
}

export default function ProposalProspectPanel({ slug, token, accent, viewer }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const viewFired = useRef(false);

  const load = async () => {
    try {
      const r = await fetch(pub(slug, token));
      if (r.status === 404) { setNotFound(true); return; }
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Failed to load (status ${r.status})`);
      setData(body);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  useEffect(() => {
    if (!data || viewFired.current) return;
    viewFired.current = true;
    whenHuman(() => { post(pub(slug, token, '/view')).catch(() => {}); });
  }, [data, slug, token]);

  if (notFound) return <Card><p style={{ margin: 0, color: '#94a3b8' }}>No proposal has been shared here yet.</p></Card>;
  if (error) return <Card><p style={{ margin: 0, color: '#dc2626' }}>{error}</p></Card>;
  if (!data) return <Card><p style={{ margin: 0, color: '#94a3b8' }}>Loading…</p></Card>;

  const { proposal } = data;
  return (
    <ProposalBody
      proposal={proposal}
      slug={slug}
      token={token}
      accent={accent}
      viewer={viewer || data.viewer}
      sellerCompany={data.room && data.room.prospectCompanyName}
      onReload={load}
    />
  );
}

function ProposalBody({ proposal, slug, token, accent, viewer, onReload }) {
  const [lineItems, setLineItems] = useState(proposal.lineItems || []);
  const [totals, setTotals] = useState(proposal.totals || computeTotals(proposal.lineItems || []));
  const [toggling, setToggling] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(proposal.acceptedAt || null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    setLineItems(proposal.lineItems || []);
    setTotals(proposal.totals || computeTotals(proposal.lineItems || []));
    setAcceptedAt(proposal.acceptedAt || null);
  }, [proposal]);

  const accepted = proposal.status === 'accepted' || !!acceptedAt;
  const expired = proposal.validUntil && !accepted && new Date(String(proposal.validUntil).slice(0, 10) + 'T23:59:59') < new Date();

  const toggleLine = async (item, selected) => {
    if (accepted || toggling) return;
    const prev = lineItems;
    const next = prev.map((l) => (l.id === item.id ? { ...l, selected } : l));
    setLineItems(next);
    setTotals(computeTotals(next));
    setToggling(item.id);
    setActionError(null);
    try {
      const r = await post(pub(slug, token, `/line-items/${encodeURIComponent(item.id)}/select`), { selected });
      if (r.lineItems) setLineItems(r.lineItems);
      if (r.totals) setTotals(r.totals);
    } catch (err) {
      setLineItems(prev);
      setTotals(computeTotals(prev));
      setActionError(err.message);
    } finally {
      setToggling(null);
    }
  };

  const accept = async () => {
    if (!window.confirm(`Accept "${proposal.title}" at ${moneyLabel(totals.totalCents)}? Your seller is notified right away; the contract itself is signed separately.`)) return;
    setAccepting(true);
    setActionError(null);
    try {
      const r = await post(pub(slug, token, '/accept'), { website_url: '' });
      setAcceptedAt(r.acceptedAt || new Date().toISOString());
    } catch (err) {
      setActionError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const quoteBlock = (proposal.blocks || []).find((b) => b.blockType === 'quote');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{proposal.title}</h2>
            <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
              {proposal.sharedAt ? `Shared ${formatDate(proposal.sharedAt)}` : ''}
              {proposal.validUntil ? ` · Valid until ${formatDate(proposal.validUntil)}` : ''}
            </div>
          </div>
          {accepted ? (
            <span style={{ fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 999, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
              Accepted{acceptedAt ? ` ${formatDate(acceptedAt)}` : ''}
            </span>
          ) : expired ? (
            <span style={{ fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>Past its valid-until date</span>
          ) : null}
        </div>
      </Card>

      {(proposal.blocks || []).map((b) => (
        <BlockView key={b.id} block={b} slug={slug} token={token} accent={accent}
          quote={b.blockType === 'quote' ? { lineItems, totals, toggling, accepted, onToggle: toggleLine, showTax: !!(b.config && b.config.showTax) } : null} />
      ))}

      {!quoteBlock && lineItems.length > 0 && (
        <QuoteTable lineItems={lineItems} totals={totals} toggling={toggling} accepted={accepted} onToggle={toggleLine} accent={accent} showTax={false} />
      )}

      {actionError && <div role="alert" style={{ padding: '10px 12px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14 }}>{actionError}</div>}

      {proposal.status === 'shared' && !accepted && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Ready to move forward?</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Accepting tells your seller you're in at {moneyLabel(totals.totalCents)}. The contract is signed separately.</div>
            </div>
            <button type="button" onClick={accept} disabled={accepting} style={{ padding: '12px 20px', borderRadius: 12, border: `1px solid ${accent}`, background: accent, color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              {accepting ? 'Sending…' : 'Accept proposal'}
            </button>
          </div>
        </Card>
      )}

      <Comments comments={proposal.comments || []} slug={slug} token={token} accent={accent} viewer={viewer} onPosted={onReload} />
    </div>
  );
}

function BlockView({ block, slug, token, accent, quote }) {
  const ref = useRef(null);
  const visibleSince = useRef(null);
  const sent = useRef(0);

  // Dwell beacon: accumulate on-screen time, flush when the block
  // scrolls out and on unmount. Capped server-side at 10 minutes.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !ref.current) return undefined;
    const flush = () => {
      if (visibleSince.current == null) return;
      const seconds = Math.round((Date.now() - visibleSince.current) / 1000);
      visibleSince.current = null;
      if (seconds < 2) return;
      sent.current += seconds;
      post(pub(slug, token, `/blocks/${encodeURIComponent(block.id)}/view`), { seconds }).catch(() => {});
    };
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && visibleSince.current == null) visibleSince.current = Date.now();
      else if (!e.isIntersecting) flush();
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => { obs.disconnect(); flush(); };
  }, [block.id, slug, token]);

  const title = block.title ? <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{block.title}</h3> : null;

  let body = null;
  if (block.blockType === 'narrative') {
    body = <InlineMarkdown text={block.config.markdown || ''} style={{ fontSize: 16, lineHeight: 1.65, color: '#334155' }} />;
  } else if (block.blockType === 'trailer') {
    body = block.unavailable || !block.trailer || !block.trailer.viewerToken
      ? <p style={{ margin: 0, color: '#94a3b8' }}>This trailer isn't available right now.</p>
      : <div style={{ borderRadius: 12, overflow: 'hidden' }}><ShareViewer key={block.trailer.viewerToken} token={block.trailer.viewerToken} embedded /></div>;
  } else if (block.blockType === 'doc') {
    body = block.unavailable || !block.doc
      ? <p style={{ margin: 0, color: '#94a3b8' }}>This document isn't available right now.</p>
      : (
        <a href={`/space/${encodeURIComponent(slug)}/doc/${encodeURIComponent(block.doc.id)}?t=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', textDecoration: 'none' }}>
          <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 8, background: `${accent}14`, color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>DOC</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontWeight: 600 }}>{block.doc.displayTitle}</span>
            {block.doc.description && <span style={{ display: 'block', fontSize: 13, color: '#64748b' }}>{block.doc.description}</span>}
          </span>
          <span style={{ fontSize: 13, color: accent, fontWeight: 600 }}>View →</span>
        </a>
      );
  } else if (block.blockType === 'media') {
    const url = block.config.url || '';
    body = /\.(mp4|webm|mov)(\?|$)/i.test(url)
      ? <video src={url} controls style={{ width: '100%', borderRadius: 12 }} />
      : <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden' }}><iframe src={url} title={block.title || 'Media'} frameBorder="0" allow="autoplay; fullscreen" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} /></div>;
  } else if (block.blockType === 'quote' && quote) {
    body = <QuoteTable {...quote} accent={accent} />;
  }

  return <div ref={ref}><Card>{title}{body}</Card></div>;
}

function QuoteTable({ lineItems, totals, toggling, accepted, onToggle, accent, showTax }) {
  const groups = new Map();
  for (const li of lineItems) {
    const g = li.isOptional && li.optionGroup ? li.optionGroup : '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(li);
  }
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480, fontSize: 14 }}>
          <thead>
            <tr style={{ color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <th style={{ ...th, width: 40 }} /><th style={th}>Item</th><th style={{ ...th, textAlign: 'right' }}>Qty</th><th style={{ ...th, textAlign: 'right' }}>Unit</th><th style={{ ...th, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {[...groups.entries()].map(([group, items]) => (
              <GroupRows key={group || '_'} group={group} items={items} toggling={toggling} accepted={accepted} onToggle={onToggle} accent={accent} />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 14, marginLeft: 'auto', maxWidth: 320, display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 18px', fontSize: 15, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
        <span>Subtotal</span><span>{moneyLabel(totals.subtotalCents)}</span>
        {totals.discountCents > 0 && <><span>Discount</span><span>−{moneyLabel(totals.discountCents)}</span></>}
        {(showTax || totals.taxCents > 0) && <><span>Tax</span><span>{moneyLabel(totals.taxCents)}</span></>}
        <strong style={{ fontSize: 18, color: '#0f172a' }}>Total</strong><strong style={{ fontSize: 18, color: '#0f172a' }}>{moneyLabel(totals.totalCents)}</strong>
        {totals.optionalUnselectedCents > 0 && <span style={{ gridColumn: '1 / -1', fontSize: 12, color: '#94a3b8' }}>Optional items not selected: {moneyLabel(totals.optionalUnselectedCents)}</span>}
      </div>
      {!accepted && lineItems.some((l) => l.isOptional) && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>Tick or untick the optional items — the total follows, and your seller sees your choices.</div>
      )}
    </div>
  );
}

function GroupRows({ group, items, toggling, accepted, onToggle, accent }) {
  return (
    <>
      {group && (
        <tr><td colSpan={5} style={{ padding: '12px 8px 4px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group}</td></tr>
      )}
      {items.map((li) => {
        const counted = li.isOptional ? li.selected !== false : true;
        return (
          <tr key={li.id} style={{ borderTop: '1px solid #f1f5f9', opacity: counted ? 1 : 0.55 }}>
            <td style={td}>
              {li.isOptional ? (
                <input type="checkbox" checked={counted} disabled={accepted || toggling === li.id} aria-label={`Include ${li.description}`}
                  onChange={(e) => onToggle(li, e.target.checked)} style={{ width: 18, height: 18, accentColor: accent, cursor: accepted ? 'default' : 'pointer' }} />
              ) : null}
            </td>
            <td style={td}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{li.description}</div>
              {(li.discountPct > 0 || li.taxRatePct > 0) && (
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{li.discountPct > 0 ? `${li.discountPct}% off` : ''}{li.discountPct > 0 && li.taxRatePct > 0 ? ' · ' : ''}{li.taxRatePct > 0 ? `${li.taxRatePct}% tax` : ''}</div>
              )}
            </td>
            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{li.quantity}</td>
            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moneyLabel(li.unitPriceCents)}</td>
            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{moneyLabel(li.lineTotalCents != null ? li.lineTotalCents : Math.round(li.quantity * li.unitPriceCents))}</td>
          </tr>
        );
      })}
    </>
  );
}

function Comments({ comments, slug, token, accent, viewer, onPosted }) {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    setError(null);
    try {
      await post(pub(slug, token, '/comments'), { body, website_url: '' });
      setDraft('');
      await onPosted();
    } catch (err) {
      setError(err.status === 429 ? 'Too many messages — give it a moment.' : err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Questions about this proposal</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>Anything you write here goes straight to your seller, tagged to this proposal.</p>
      {comments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {comments.map((m) => {
            const mine = m.senderKind === 'prospect';
            return (
              <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '10px 12px', borderRadius: 12, background: mine ? `${accent}14` : '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{m.senderName || (mine ? 'You' : 'Seller')} · {formatWhen(m.createdAt)}</div>
                <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap' }}>{m.body}</div>
              </div>
            );
          })}
        </div>
      )}
      {viewer?.isPreview ? (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fef3c7', color: '#92400e', fontSize: 13, lineHeight: 1.45 }}>
          Questions can’t be sent from a preview. To reach this person, share the Decision Room with them — the preview is only for checking how it looks.
        </div>
      ) : (
      <form onSubmit={send}>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={4000} placeholder="Ask a question or leave a note…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', color: '#0f172a', background: '#fff' }} />
        {error && <div role="alert" style={{ marginTop: 8, fontSize: 13, color: '#991b1b' }}>{error}</div>}
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={posting || !draft.trim()} style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${accent}`, background: accent, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: posting || !draft.trim() ? 0.6 : 1 }}>
            {posting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
      )}
    </Card>
  );
}

function Card({ children }) {
  return <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22 }}>{children}</div>;
}
const th = { textAlign: 'left', padding: '6px 8px', fontWeight: 600 };
const td = { padding: '10px 8px', verticalAlign: 'top' };
